import { useState, useEffect } from 'react'
import { useParams, useNavigate, useSearchParams } from 'react-router-dom'
import { CheckCircle, XCircle, Clock, ArrowRight, FileText, User, Calendar } from 'lucide-react'
import { authAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import BottomActionBar from '../../components/BottomActionBar'
import { clinicianName } from '../../utils/clinicianName.js'
import { OrellaSpinner } from '../../components/OrellaLoader' // s123: the app's one spinner

// Brand colors
const BRAND_NAVY = '#15354F'
const BRAND_BLUE = '#46A8F9'

export default function JoinInvitePage() {
  // Support both URL param (/join/:inviteCode) and query params (/join?invite=X&case=Y)
  const { inviteCode: paramInviteCode } = useParams()
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  
  // Get invite code from either param or query string
  const inviteCode = paramInviteCode || searchParams.get('invite')
  const caseId = searchParams.get('case')
  
  const { t } = useI18n()
  
  const [status, setStatus] = useState('loading') // 'loading', 'valid', 'expired', 'invalid', 'error'
  const [inviteData, setInviteData] = useState(null)
  const [caseData, setCaseData] = useState(null)
  const [error, setError] = useState('')
  
  useEffect(() => {
    verifyInviteAndCase()
  }, [inviteCode, caseId])
  
  // ★★ PROD-611 (v242.238.1). WHO THE PATIENT IS COMES FROM THE INVITE CODE, NEVER FROM THE CASE.
  //   orella-api D5c S9 (PROD-212, v243.284.0) cut the logged-out case preview to
  //   { specialty, focusArea, doctor }: a case id is not a secret. The invite link is always
  //   /join?invite=X&case=Y, and this function used to build the patient's name, e-mail and phone
  //   from caseResult.patient and never call verifyInvite while the case answered — so from D5c on
  //   the "Your information" rows arrived empty and so did SignUpScreen's required name fields,
  //   which the patient then had to type for a registration (register-invite) that ignores them.
  //   Now: with an invite code, the prefill is verifyInvite's (GET /auth/verify-invite/<code>, on
  //   the API's PUBLIC_ROUTES); the case preview supplies only specialty, focus area and doctor.
  //   When the invite does not verify, the paths below are what they were before.
  //   Held by scripts/check-invite-prefill.mjs.
  const verifyInviteAndCase = async () => {
    let caseResult = null
    if (caseId) {
      try {
        const preview = await authAPI.getCasePreview(caseId)
        console.log('[JoinInvite] Case preview result:', !!(preview && !preview.error)) // PROD-903: presence only
        if (preview && !preview.error) caseResult = preview
      } catch (caseErr) {
        console.log('[JoinInvite] Could not fetch case preview:', caseErr)
      }
    }
    const caseFields = caseResult ? {
      doctorName: caseResult.doctor ? clinicianName(caseResult.doctor, 'display', 'en') : null,
      practiceName: caseResult.doctor?.practiceName,
      specialty: caseResult.specialty,
      focusArea: caseResult.focusArea
    } : null

    let invite = null
    let inviteErr = null
    if (inviteCode) {
      try {
        invite = await authAPI.verifyInvite(inviteCode)
      } catch (err) {
        inviteErr = err
      }
    }

    if (invite?.valid) {
      if (caseResult) setCaseData(caseResult)
      setInviteData({
        valid: true,
        firstName: invite.firstName || '',
        lastName: invite.lastName || '',
        email: invite.email || '',
        phone: invite.phone || '',
        doctorName: caseFields?.doctorName || invite.doctorName || null,
        practiceName: caseFields?.practiceName,
        specialty: caseFields?.specialty,
        focusArea: caseFields?.focusArea
      })
      setStatus('valid')
      return
    }

    // The invite did not verify (or there is none): a case that answered still shows its details,
    // as before; the patient's own details stay empty — the case preview no longer carries them.
    // ★ PROD-895: NOT when the invite code answered "not valid" (API 404 `{valid:false}` or
    //   410 `{valid:false, expired:true}`, see authAPI.verifyInvite). An expired or unknown code
    //   with a case preview used to render the preview with empty "Your information" rows; it
    //   now falls through to the expired / invalid screens below. A thrown verifyInvite (network,
    //   5xx, the API's 400 alreadyRegistered) keeps the fallback. Held by check-invite-prefill IP-6.
    const inviteRefused = Boolean(inviteCode && invite && invite.valid === false)
    if (caseResult && !inviteRefused) {
      setCaseData(caseResult)
      setInviteData({ valid: true, firstName: '', lastName: '', email: '', phone: '', ...caseFields })
      setStatus('valid')
      return
    }

    // Invite code verification when no case answered
    if (inviteCode && (inviteErr || !invite)) {
      console.error('[JoinInvite] Error verifying invite:', inviteErr)
      setStatus('error')
      setError(t('errors2.unableToVerifyInvite'))
    } else if (inviteCode) {
      if (invite.expired) {
        setStatus('expired')
        setInviteData(invite)
      } else {
        // ★ PROD-898: the screen shows the translated sentence, never invite.error — that is
        //   authAPI.verifyInvite's hard-coded English ("Invitation not found") or the API's
        //   English, which every language read before. Held by check-invite-prefill IP-6 / IP-12.
        setStatus('invalid')
        setError(t('joinInvite.invalidLinkFallback'))
      }
    } else if (!caseId) {
      // No case ID and no invite code
      setStatus('invalid')
      setError(t('errors2.invalidInviteLink'))
    } else {
      // Had case ID but lookup failed
      setStatus('error')
      setError(t('errors2.unableToLoadCase'))
    }
  }
  
  // ★★★ PROD-614 (v242.238.2). NO PATIENT FIELD ON A URL. This used to put the invited patient's
  //   name, e-mail and phone on the /signup query string, where they reach browser history, the
  //   Referer header and edge logs. From orella-api D5c those values were empty; with PROD-611
  //   (verifyInvite on /join) they would have been filled again. /signup now gets the invite code
  //   only, and SignUpScreen.loadInvitePrefill asks verifyInvite for the details itself. The case
  //   id (`pendingCase`) is no longer passed either: measured 2026-10-06, nothing in this repo
  //   reads it from the /signup URL. Held by scripts/check-invite-prefill.mjs (IP-7).
  const handleContinue = () => {
    navigate(inviteCode ? `/signup?${new URLSearchParams({ inviteCode })}` : '/signup')
  }
  
  const handleSignIn = () => {
    // If they have a pending case, pass it to signin too
    if (caseId) {
      navigate(`/signin?pendingCase=${caseId}`)
    } else {
      navigate('/signin')
    }
  }
  
  // Loading state
  if (status === 'loading') {
    return (
      <div className="screen-min-h flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="w-20 h-20 rounded-2xl flex items-center justify-center mb-6" style={{ backgroundColor: BRAND_NAVY }}>
          <OrellaSpinner size={40} tone="dark" />
        </div>
        <h1 className="text-xl font-bold text-slate-900 mb-2">{t('joinInvite.verifyingInvitation')}</h1>
        <p className="text-slate-500 text-center">{t('joinInvite.pleaseWaitWhileWeVerify')}</p>
      </div>
    )
  }
  
  // Valid invitation
  if (status === 'valid' && inviteData) {
    return (
      <div className="screen-min-h flex flex-col bg-white">
        {/* Header */}
        <div className="relative h-48 flex-shrink-0">
          <div className="absolute inset-0" style={{ background: `linear-gradient(135deg, ${BRAND_NAVY} 0%, ${BRAND_BLUE} 100%)` }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <img 
              src="/icons/icon-192x192.png" 
              alt="Orella" 
              className="w-20 h-20 rounded-2xl"
              onError={(e) => { e.target.style.display = 'none' }}
            />
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 px-6 pt-8 pb-4 -mt-6 bg-white rounded-t-3xl relative">
          <div className="max-w-md mx-auto">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-emerald-600" />
              </div>
            </div>
            
            <h1 className="text-2xl font-bold text-slate-900 text-center mb-2">
              {t('auth.welcomeToOrella')}
            </h1>
            
            <p className="text-slate-600 text-center mb-6">
              {inviteData.doctorName ? (
                <>{t('joinInvite.doctorInvitedNamed', { doctor: inviteData.doctorName })}</>
              ) : (
                <>{t('joinInvite.doctorInvitedYou')}</>
              )}
            </p>
            
            {/* Case Info Card (if we have case data) */}
            {(caseData || inviteData.specialty) && (
              <div className="bg-blue-50 border-2 border-blue-200 rounded-2xl p-5 mb-6">
                <div className="flex items-center gap-2 mb-3">
                  <FileText className="w-5 h-5 text-blue-600" />
                  <h3 className="text-sm font-semibold text-blue-900 uppercase tracking-wide">
                    {t('joinInvite.yourVisitDetails', 'Your Visit Details')}
                  </h3>
                </div>
                <div className="space-y-2">
                  {inviteData.doctorName && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('joinInvite.doctor')}</span>
                      <span className="font-medium text-slate-900">{inviteData.doctorName}</span>
                    </div>
                  )}
                  {(caseData?.specialty || inviteData.specialty) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('joinInvite.specialty')}</span>
                      <span className="font-medium text-slate-900">
                        {(caseData?.specialty || inviteData.specialty || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  )}
                  {(caseData?.focusArea || inviteData.focusArea) && (
                    <div className="flex justify-between">
                      <span className="text-slate-500">{t('joinInvite.focus')}</span>
                      <span className="font-medium text-slate-900">
                        {(caseData?.focusArea || inviteData.focusArea || '').replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-xs text-blue-600 mt-3">
                  {t('joinInvite.caseAppearAfterSignup')}
                </p>
              </div>
            )}
            
            {/* Patient Info Card */}
            <div className="bg-slate-50 rounded-2xl p-5 mb-6">
              <div className="flex items-center gap-2 mb-3">
                <User className="w-5 h-5 text-slate-500" />
                <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wide">
                  {t('joinInvite.yourInformation', 'Your Information')}
                </h3>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-slate-500">{t('joinInvite.name')}</span>
                  <span className="font-medium text-slate-900">
                    {inviteData.firstName} {inviteData.lastName}
                  </span>
                </div>
                {inviteData.email && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('joinInvite.email')}</span>
                    <span className="font-medium text-slate-900">{inviteData.email}</span>
                  </div>
                )}
                {inviteData.phone && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">{t('joinInvite.phone')}</span>
                    <span className="font-medium text-slate-900">{inviteData.phone}</span>
                  </div>
                )}
              </div>
            </div>
            
            {/* What's Next */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-8">
              <h3 className="font-semibold text-slate-900 mb-3">{t('joinInvite.whatsNext')}</h3>
              <ul className="space-y-3 text-sm text-slate-600">
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: BRAND_BLUE }}>1</span>
                  <span>{t('joinInvite.createYourPasswordToSecure')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: BRAND_BLUE }}>2</span>
                  <span>{t('joinInvite.completeYourHealthProfile')}</span>
                </li>
                <li className="flex items-start gap-3">
                  <span className="w-6 h-6 rounded-full text-white text-xs flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: BRAND_BLUE }}>3</span>
                  <span>{t('joinInvite.describeYourSymptomsForYour')}</span>
                </li>
              </ul>
            </div>
            
          </div>
        </div>

        {/* Actions. PROD-1194: pinned at the bottom of the screen. */}
        <BottomActionBar className="[--bar-px:24px] flex flex-col items-center">
          <div className="w-full max-w-md">
            <button
              onClick={handleContinue}
              className="w-full py-4 rounded-xl text-white font-semibold flex items-center justify-center gap-2 mb-3 shadow-lg"
              style={{ backgroundColor: BRAND_NAVY }}
            >
              {t('joinInvite.completePreVisitIntake')}
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <p className="text-center text-sm text-slate-500">
              {t('joinInvite.alreadyHaveAnAccount', 'Already have an account?')}{' '}
              <button onClick={handleSignIn} className="font-medium" style={{ color: BRAND_BLUE }}>
                {t('joinInvite.signIn', 'Sign in')}
              </button>
            </p>
          </div>
        </BottomActionBar>
      </div>
    )
  }
  
  // Expired invitation
  if (status === 'expired') {
    return (
      <div className="screen-min-h flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-amber-50">
        <div className="w-20 h-20 rounded-2xl bg-amber-100 flex items-center justify-center mb-6">
          <Clock className="w-10 h-10 text-amber-600" />
        </div>
        <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">{t('joinInvite.invitationExpired')}</h1>
        <p className="text-slate-600 text-center mb-8 max-w-sm">
          {t('joinInvite.invitationExpiredBody', "This invitation has expired. Please contact your doctor's office to request a new invitation.")}
        </p>
        
        <div className="w-full max-w-xs space-y-3">
          <button
            onClick={handleSignIn}
            className="w-full py-3 rounded-xl text-white font-semibold"
            style={{ backgroundColor: BRAND_NAVY }}
          >
            {t('joinInvite.signInInstead')}
          </button>
          <button
            onClick={() => navigate('/signup')}
            className="w-full py-3 rounded-xl font-semibold border-2"
            style={{ borderColor: BRAND_NAVY, color: BRAND_NAVY }}
          >
            {t('joinInvite.createNewAccount')}
          </button>
        </div>
      </div>
    )
  }
  
  // Invalid or error state
  return (
    <div className="screen-min-h flex flex-col items-center justify-center p-6 bg-gradient-to-br from-slate-50 to-red-50">
      <div className="w-20 h-20 rounded-2xl bg-red-100 flex items-center justify-center mb-6">
        <XCircle className="w-10 h-10 text-red-600" />
      </div>
      <h1 className="text-2xl font-bold text-slate-900 mb-2 text-center">{t('joinInvite.invalidInvitation')}</h1>
      <p className="text-slate-600 text-center mb-8 max-w-sm">
        {error || t('joinInvite.invalidLinkFallback')}
      </p>
      
      <div className="w-full max-w-xs space-y-3">
        <button
          onClick={handleSignIn}
          className="w-full py-3 rounded-xl text-white font-semibold"
          style={{ backgroundColor: BRAND_NAVY }}
        >
          {t('joinInvite.signInInstead')}
        </button>
        <button
          onClick={() => navigate('/signup')}
          className="w-full py-3 rounded-xl font-semibold border-2"
          style={{ borderColor: BRAND_NAVY, color: BRAND_NAVY }}
        >
          {t('joinInvite.createNewAccount')}
        </button>
      </div>
    </div>
  )
}
